import {
    deriveLegendData,
    PortfolioPlDataItem,
} from 'widgets/portfolioPl/portfolioPlData';
import { PortfolioPlDimensions } from 'widgets/portfolioPl/dimensions';
import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import React, { useContext } from 'react';
import AppContext from 'app/AppContext';
import { usePortfolioPlConfigSlice } from 'widgets/portfolioPl/store/config/configSlice';

import classNames from 'classnames';

import './PortfolioPlLeavesChartLegends.scss';
import ShowHideToggle from 'utils/components/ShowHideToggle/ShowHideToggle';
import {
    netLeavesTooltip,
    buyLeavesTooltip,
    sellLeavesTooltip,
} from 'widgets/portfolioPl/chart/leaves/help/PortfolioPlLeavesChartLegendTooltips';
import PortfolioPlLeavesChartHelp from 'widgets/portfolioPl/chart/leaves/help/PortfolioPlLeavesChartHelp';
import { PortfolioPlWidgetLegends } from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import { WidgetTimeSeriesLegend } from 'widgets/WidgetTimeSeriesLegend/WidgetTimeSeriesLegend';
import { usePortfolioPlLegendStatus } from 'widgets/portfolioPl/chart/hooks';
import { CurrencyFormat } from '@instinet-analytics/core-lib';

export interface PortfolioPlLeavesChartLegendsProps {
    containerLeft: number;
    data: PortfolioPlDataItem[];
    dimensions: PortfolioPlDimensions;
}

export function PortfolioPlLeavesChartLegends(
    props: PortfolioPlLeavesChartLegendsProps
) {
    const { containerLeft, data, dimensions } = props;
    const time = usePointedTime('PortfolioPlLeavesChartLegends');
    const { numberFormat, currencyFormat: appCurrencyFormat } =
        useContext(AppContext);

    const { useToggleConfigOption } = usePortfolioPlConfigSlice();

    const { value: collapsed, toggle: toggleCollapsed } =
        useToggleConfigOption('leavesCollapsed');

    // DO not apply an exchange rate https://jira.isn.instinet.com:8443/browse/IND2-3128
    const currencyFormat = new CurrencyFormat(numberFormat);
    currencyFormat.prefix = appCurrencyFormat.prefix;

    const { buyLeaves, sellLeaves, netLeaves } = deriveLegendData(
        time,
        data,
        numberFormat,
        currencyFormat
    );

    const { showLeavesNetLegend, showLeavesBuysLegend, showLeavesSellsLegend } =
        usePortfolioPlLegendStatus();

    const { hideLeavesLegends } = usePortfolioPlLegendStatus();
    if (hideLeavesLegends) {
        return <></>;
    }
    const {
        leavesChart: { legendsMarginTop },
    } = dimensions;

    return (
        <div
            className={classNames(
                'PortfolioPlLeavesChartLegends',
                'legend-group'
            )}
            style={{
                marginTop: legendsMarginTop + 'px',
            }}
        >
            {showLeavesNetLegend && (
                <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                    className={classNames(
                        'PortfolioPlLeavesChartLegend',
                        'net'
                    )}
                    label={'Net'}
                    hideSeriesPropertyName={'leavesHideNet'}
                    value={netLeaves}
                    containerLeft={containerLeft}
                    tooltip={netLeavesTooltip}
                />
            )}

            {showLeavesBuysLegend && (
                <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                    className={classNames(
                        'PortfolioPlLeavesChartLegend',
                        'buys'
                    )}
                    label={'Buys'}
                    value={buyLeaves}
                    containerLeft={containerLeft}
                    hideSeriesPropertyName={'leavesHideBuys'}
                    tooltip={buyLeavesTooltip}
                />
            )}

            {showLeavesSellsLegend && (
                <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                    alignDropdown={'right'}
                    className={classNames(
                        'PortfolioPlLeavesChartLegend',
                        'sells'
                    )}
                    label={'Sells'}
                    value={sellLeaves}
                    containerLeft={containerLeft}
                    hideSeriesPropertyName={'leavesHideSells'}
                    tooltip={sellLeavesTooltip}
                />
            )}
            <ShowHideToggle
                status={collapsed ? 'show' : 'hide'}
                onToggle={toggleCollapsed}
            />
            <PortfolioPlLeavesChartHelp />
        </div>
    );
}
